import { useState } from 'react';
import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export function NotificationBell() {
  const { isSupported, isSubscribed, permission, isLoading, subscribe, unsubscribe } = usePushNotifications();
  const [isOpen, setIsOpen] = useState(false);

  // Don't render if not supported
  if (!isSupported) return null;

  const handleToggle = async () => {
    if (isSubscribed) {
      const result = await unsubscribe();
      if (result.success) {
        toast.success('Notifications disabled');
      } else {
        toast.error('Failed to disable notifications');
      }
    } else {
      const result = await subscribe();
      if (result.success) {
        toast.success('Notifications enabled! Welcome aboard.');
        setIsOpen(false);
      } else if (result.error === 'Notification permission denied') {
        toast.error('Please enable notifications in your browser settings');
      } else {
        toast.error('Failed to enable notifications');
      }
    }
  };

  const BellIcon = isSubscribed ? BellRing : (permission === 'denied' ? BellOff : Bell);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 text-muted-foreground hover:text-foreground transition-colors touch-target tap-highlight-none"
          aria-label="Notifications"
        >
          <BellIcon className="h-5 w-5" />
          {isSubscribed && (
            <span className="absolute top-1 right-1 h-2 w-2 bg-primary rounded-full animate-pulse" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-4" align="end">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <BellIcon className="h-5 w-5 text-primary" />
            <h4 className="font-semibold text-sm">Push Notifications</h4>
          </div>
          
          {permission === 'denied' ? (
            <p className="text-xs text-muted-foreground">
              Notifications are blocked. Please enable them in your browser settings.
            </p>
          ) : isSubscribed ? (
            <>
              <p className="text-xs text-muted-foreground">
                You're subscribed! You'll get alerts for new shows, merch drops, and exclusive updates.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleToggle}
                disabled={isLoading}
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Turn Off Notifications
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                Never miss a show! Get instant alerts for new tour dates, merch drops, and exclusive updates.
              </p>
              <Button
                size="sm"
                className="w-full"
                onClick={handleToggle}
                disabled={isLoading}
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Enable Notifications
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
